import React, { useEffect, useId, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import HeroBgAnimation from '../HeroBgAnimation'
import {
  HeroContainer,
  HeroBg,
  HeroLeftContainer,
  Img,
  HeroRightContainer,
  HeroInnerContainer,
  TextLoop,
  Title,
  Span,
  SubTitle,
  SocialMediaIcons,
  SocialMediaIcon,
  ResumeButton,
  CTARow,
  SecondaryButton,
  ReadMoreButton,
  ResumeMenu,
  ResumeMenuLink,
  ScrollIndicator,
  SocialMoreWrapper,
  SocialMoreMenu,
} from './HeroStyle'
import Typewriter from 'typewriter-effect';
import { Bio } from '../../data/constants';
import { slideInLeft, slideInRight, staggerContainer, staggerItem } from '../../utils/animations';
import { trackResumeDownload } from '../../utils/analytics';
import { useTranslation } from 'react-i18next';
import { 
  FaGithub, 
  FaLinkedin, 
  FaTwitter, 
  FaInstagram, 
  FaFacebook, 
  FaYoutube,
  FaChevronDown,
  FaEllipsisH,
} from 'react-icons/fa';

const primarySocial = [
  { icon: <FaGithub />, url: Bio.github, label: "GitHub" },
  { icon: <FaLinkedin />, url: Bio.linkedin, label: "LinkedIn" },
  { icon: <FaTwitter />, url: Bio.twitter, label: "Twitter" },
];

const moreSocial = [
  { icon: <FaInstagram />, url: Bio.insta, label: "Instagram" },
  { icon: <FaFacebook />, url: Bio.facebook, label: "Facebook" },
  { icon: <FaYoutube />, url: Bio.youtube, label: "YouTube" },
];

const HeroSection = () => {
    const { t } = useTranslation();
    const [isResumeMenuOpen, setIsResumeMenuOpen] = useState(false);
    const [isSocialMoreOpen, setIsSocialMoreOpen] = useState(false);
    const [bioExpanded, setBioExpanded] = useState(false);
    const resumeMenuId = useId();
    const resumeMenuRef = useRef(null);
    const socialMoreRef = useRef(null);

    useEffect(() => {
        if (!isResumeMenuOpen && !isSocialMoreOpen) return;

        const handlePointerDown = (e) => {
            if (resumeMenuRef.current?.contains(e.target)) return;
            if (socialMoreRef.current?.contains(e.target)) return;
            setIsResumeMenuOpen(false);
            setIsSocialMoreOpen(false);
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setIsResumeMenuOpen(false);
                setIsSocialMoreOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isResumeMenuOpen, isSocialMoreOpen]);

    const bioText = bioExpanded
        ? t('hero.descriptionFull', { defaultValue: t('hero.description') })
        : t('hero.description');

    return (
        <div id="about">
            <HeroContainer>
                <HeroBg>
                    <HeroBgAnimation />
                </HeroBg>
                <HeroInnerContainer
                    as={motion.div}
                    variants={staggerContainer}
                    initial="initial"
                    animate="animate"
                >
                    <HeroLeftContainer 
                        id="Left"
                        as={motion.div}
                        variants={slideInRight}
                    >
                        <motion.div variants={staggerItem}>
                            <Title>{t('hero.greeting')} <br /> {Bio.name}</Title>
                        </motion.div>
                        <motion.div variants={staggerItem}>
                            <TextLoop>
                                {t('hero.iAmA')}
                                <Span>
                                    <Typewriter
                                        options={{
                                            strings: t('hero.roles', { returnObjects: true, defaultValue: Bio.roles }),
                                            autoStart: true,
                                            loop: true,
                                        }}
                                    />
                                </Span>
                            </TextLoop>
                        </motion.div>
                        <motion.div variants={staggerItem}>
                            <SubTitle>{bioText}</SubTitle>
                            {t('hero.descriptionFull') && t('hero.descriptionFull') !== t('hero.description') && (
                                <ReadMoreButton
                                    type="button"
                                    onClick={() => setBioExpanded((v) => !v)}
                                    aria-expanded={bioExpanded}
                                >
                                    {bioExpanded ? t('hero.readLess') : t('hero.readMore')}
                                </ReadMoreButton>
                            )}
                        </motion.div>
                        <motion.div variants={staggerItem}>
                            <CTARow>
                                <div
                                    ref={resumeMenuRef}
                                    style={{ position: 'relative', flex: '1 1 160px', maxWidth: 300 }}
                                >
                                    <ResumeButton
                                        href="#resume"
                                        aria-label="Choose resume language"
                                        aria-haspopup="menu"
                                        aria-expanded={isResumeMenuOpen}
                                        aria-controls={resumeMenuId}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsResumeMenuOpen((v) => !v);
                                        }}
                                    >
                                        {t('hero.resume')}
                                    </ResumeButton>
                                    {isResumeMenuOpen && (
                                        <ResumeMenu
                                            id={resumeMenuId}
                                            role="menu"
                                            aria-label="Resume language options"
                                        >
                                            <ResumeMenuLink
                                                role="menuitem"
                                                href={Bio.resumeKorean}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                type="application/pdf"
                                                onClick={() => {
                                                    trackResumeDownload();
                                                    setIsResumeMenuOpen(false);
                                                }}
                                            >
                                                {t('hero.resumeKorean')}
                                            </ResumeMenuLink>
                                            <ResumeMenuLink
                                                role="menuitem"
                                                href={Bio.resumeEnglish}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                type="application/pdf"
                                                onClick={() => {
                                                    trackResumeDownload();
                                                    setIsResumeMenuOpen(false);
                                                }}
                                            >
                                                {t('hero.resumeEnglish')}
                                            </ResumeMenuLink>
                                        </ResumeMenu>
                                    )}
                                </div>
                                <SecondaryButton href="#projects">
                                    {t('hero.viewProjects')}
                                </SecondaryButton>
                                <SecondaryButton href="#contact">
                                    {t('hero.contactMe')}
                                </SecondaryButton>
                            </CTARow>
                        </motion.div>
                        <motion.div variants={staggerItem}>
                            <SocialMediaIcons>
                                {primarySocial.map((social) => (
                                    <SocialMediaIcon
                                        key={social.label}
                                        as={motion.a}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={social.label}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {social.icon}
                                    </SocialMediaIcon>
                                ))}
                                <SocialMoreWrapper ref={socialMoreRef}>
                                    <SocialMediaIcon
                                        as="button"
                                        type="button"
                                        aria-label={t('hero.moreSocial')}
                                        aria-expanded={isSocialMoreOpen}
                                        aria-haspopup="menu"
                                        onClick={() => setIsSocialMoreOpen((v) => !v)}
                                        style={{ border: 'none' }}
                                    >
                                        <FaEllipsisH />
                                    </SocialMediaIcon>
                                    {isSocialMoreOpen && (
                                        <SocialMoreMenu role="menu" aria-label={t('hero.moreSocial')}>
                                            {moreSocial.map((social) => (
                                                <ResumeMenuLink
                                                    key={social.label}
                                                    role="menuitem"
                                                    href={social.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => setIsSocialMoreOpen(false)}
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                                                >
                                                    {social.icon} {social.label}
                                                </ResumeMenuLink>
                                            ))}
                                        </SocialMoreMenu>
                                    )}
                                </SocialMoreWrapper>
                            </SocialMediaIcons>
                        </motion.div>
                        <ScrollIndicator href="#skills" aria-label={t('hero.scrollHint')}>
                            {t('hero.scrollHint')}
                            <FaChevronDown />
                        </ScrollIndicator>
                    </HeroLeftContainer>

                    <HeroRightContainer 
                        id="Right"
                        as={motion.div}
                        variants={slideInLeft}
                    >
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Img 
                                src="/SiyamImage.webp" 
                                alt="Siyam Uddin - Java Backend Developer" 
                                loading="eager" 
                                fetchpriority="high"
                                width="400"
                                height="400"
                                style={{ aspectRatio: '1/1' }}
                            />
                        </motion.div>
                    </HeroRightContainer>
                </HeroInnerContainer>

            </HeroContainer>
        </div>
    )
}

export default HeroSection
